import React, { useEffect } from "react";
import { Box } from "@material-ui/core";
import {
  CTextField,
  CSelect,
  HFlex,
  ProgressButton,
  Text,
  DialogSuccessNotice,
  useErrorContext,
  MultiSelect,
  ErrorBox,
} from "shared";
import { FormProvider, useForm } from "react-hook-form";
import { PhoneNumberField, withOptionalPhone } from "shared/PhoneNumberInput";
import { useTranslation } from "react-i18next";
import { Send } from "assets";
import { delay } from "utils/misc";
import { Country, getCountryCode, isUsMarket, TimeZoneIana } from "utils/market";
import { FORM_NOTIFICATION_DELAY } from "theme";
import { useMountState } from "utils/hooks";
import useInvitePhysician, { Vars } from "routes/other/queries/useInvitePhysician";
import { countriesEU, timeZoneIanaByCountry, timeZonesEUIana, timeZonesUSIana } from "../helpers";
import { useStates } from "shared/ValueTable";

interface Props {
  onClose: () => void;
}

const UsLicensesField: React.VFC = () => {
  const { t } = useTranslation();
  const licenses = useStates();

  return (
    <MultiSelect
      disableCloseOnSelect
      getOptionLabel={({ value, code }) => `${value} (${code})`}
      getOptionValue={opt => opt.code}
      label={t`form.state_licenses`}
      loading={licenses.isLoading}
      margin="normal"
      name="us_licenses"
      noOptionsText={licenses.error ? <ErrorBox error={licenses.error} /> : ""}
      options={licenses.data ?? []}
    />
  );
};

export const InvitePhysicianForm: React.VFC<Props> = ({ onClose }) => {
  const form = useForm<Vars>();
  const { t } = useTranslation();
  const setError = useErrorContext();
  const getMountState = useMountState();
  const country = form.watch("country", isUsMarket ? Country.US : Country.SE);
  const hasPhoneLogin = country === Country.US || country === Country.UK;
  const { setValue } = form;

  useEffect(() => {
    const timeZone = timeZoneIanaByCountry[country];
    if (timeZone) {
      setValue("timezone", timeZone);
    }
  }, [country, setValue]);

  const { mutateAsync, isLoading, isError, data } = useInvitePhysician();

  const submit = form.handleSubmit(async fields => {
    try {
      await mutateAsync(withOptionalPhone(fields));
      if (!getMountState()) return;
      await delay(FORM_NOTIFICATION_DELAY);
      if (!getMountState()) return;
      onClose();
    } catch (err) {
      setError(t`errors.could_not_send_invite`, err);
    }
  });

  if (!isError && data)
    return (
      <DialogSuccessNotice
        heading={t("patients.invite_sent")}
        body={`${t("form.invitation_successfully_sent_to")} ${data.email}`}
        onClose={onClose}
      />
    );

  return (
    <Box mx={3}>
      <Text heading bold mb={3} id="invite-doctor">
        {t("form.invite_physician")}
      </Text>
      <FormProvider {...form}>
        <form onSubmit={submit}>
          <CTextField
            autoFocus
            fullWidth
            label={t("form.email")}
            margin="normal"
            name="email"
            required
            size="small"
            type="email"
          />

          {hasPhoneLogin && (
            <PhoneNumberField
              containerStyle={{ width: "100%", margin: "16px 0 8px" }}
              country={getCountryCode(country).toLowerCase()}
              inputStyle={{ width: "100%" }}
            />
          )}

          <HFlex gap={2}>
            <CTextField
              fullWidth
              label={t("form.first_name")}
              margin="normal"
              name="first_name"
              required
              size="small"
            />
            <CTextField fullWidth label={t("form.last_name")} margin="normal" name="last_name" required size="small" />
          </HFlex>

          {isUsMarket && (
            <CTextField fullWidth label={t("form.npi")} margin="normal" name="npi" required size="small" />
          )}

          <CSelect
            defaultValue={isUsMarket ? null : TimeZoneIana.Stockholm}
            fullWidth
            items={isUsMarket ? timeZonesUSIana : timeZonesEUIana}
            label={t("form.home_timezone")}
            margin="dense"
            name="timezone"
            required
            size="small"
          >
            {({ label }) => <Text>{label}</Text>}
          </CSelect>

          {isUsMarket ? (
            <>
              <UsLicensesField />
              <CTextField defaultValue={Country.US} name="country" noClear required type="hidden" variant="standard" />
            </>
          ) : (
            <CSelect
              defaultValue={Country.SE}
              fullWidth
              items={countriesEU}
              label={t("form.country")}
              margin="dense"
              name="country"
              required
              size="small"
            >
              {({ label }) => <Text>{label}</Text>}
            </CSelect>
          )}

          <HFlex justifyContent="center" my={6}>
            <ProgressButton loading={isLoading} endIcon={<Send width={20} />}>
              {t("buttons.send_invitation")}
            </ProgressButton>
          </HFlex>
        </form>
      </FormProvider>
    </Box>
  );
};
